'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { getMediaData } from '@/lib/utils/media.utils';
import type { LandscapeEmbed } from '@/types';

/**
 * Renders a social or video embed as a click-to-load facade.
 *
 * Nothing from the provider is requested until the visitor asks for it. That
 * keeps a third-party tracker off the page by default, and it keeps the block
 * fast, but the real reason is indexing: a raw embed is an empty iframe to a
 * crawler, so a post that proves something about the practice would otherwise
 * contribute nothing to how this site is described elsewhere. The context and
 * attribution below are ordinary text in the page, and they stay readable
 * whether or not anyone clicks.
 */

type Props = { layout: LandscapeEmbed };

function toEmbedSrc(provider: string, url: string): string | null {
  try {
    const u = new URL(url);

    if (provider === 'youtube') {
      const id =
        u.searchParams.get('v') ||
        (u.hostname.includes('youtu.be') ? u.pathname.slice(1) : '');
      return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1` : null;
    }

    if (provider === 'vimeo') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : null;
    }

    if (provider === 'x') {
      // X has no iframe player. Its embed needs the platform script, so the
      // facade opens the post in a new tab rather than pretending otherwise.
      return null;
    }
  } catch {
    return null;
  }
  return null;
}

export function Landscape_Embed({ layout }: Props) {
  const [active, setActive] = useState(false);

  const poster = getMediaData(layout.poster);
  const src = toEmbedSrc(layout.provider, layout.url);
  const isX = layout.provider === 'x';

  const label = isX ? 'Open post on X' : 'Play video';

  return (
    <section className="w-full py-10 md:py-16">
      {layout.title && (
        <h2 className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-4">
          [ {layout.title} ]
        </h2>
      )}

      <div className="relative w-full overflow-hidden rounded border border-zinc-200 bg-zinc-100 aspect-video">
        {active && src ? (
          <iframe
            src={src}
            title={layout.title || 'Embedded video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <>
            {poster.url && (
              <Image
                src={poster.url}
                alt={poster.alt || layout.title || 'Embedded post'}
                fill
                sizes="(max-width: 768px) 100vw, 80vw"
                className="object-cover"
              />
            )}

            {/*
              An anchor for X, because the destination is a real page and a
              middle-click or a crawler should both reach it. A button for the
              players, because nothing navigates.
            */}
            {isX || !src ? (
              <a
                href={layout.url}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <span className="rounded bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-black">
                  {label}
                </span>
              </a>
            ) : (
              <button
                type="button"
                onClick={() => setActive(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <span className="rounded bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-black">
                  {label}
                </span>
              </button>
            )}

            {layout.timestamp && (
              <span className="pointer-events-none absolute bottom-3 right-3 rounded bg-black/75 px-2 py-1 font-mono text-[10px] tracking-wider text-white">
                {layout.timestamp}
              </span>
            )}
          </>
        )}
      </div>

      {(layout.context || layout.attribution) && (
        <div className="mt-4 max-w-2xl">
          {layout.context && (
            <p className="font-mono text-[11px] leading-relaxed text-zinc-700">
              {layout.context}
            </p>
          )}
          {layout.attribution && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
              {layout.attribution}
              {' · '}
              <a
                href={layout.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-zinc-700"
              >
                View original
              </a>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
